import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import {
  formatStoreListLabel,
  storesInCone,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import {
  MEMPHIS_LINKED_EVENT_NAME,
  MEMPHIS_THRESHOLD_DESCRIPTION,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import {
  CFA_HELENE_ORG_EVENT_DEMO_KEY,
  orgEventKeys,
  type OrgEvent,
} from 'hooks/orgEvents/orgEventHooks';

/** Shared locks so React Strict Mode remounts do not double-create. */
const ensureLocks = new Map<string, Promise<unknown>>();

function heleneConeCentroid(): { lon: number; lat: number } {
  const stores = storesInCone();
  if (stores.length === 0) {
    return { lon: -85.2, lat: 27.8 };
  }
  const lon = stores.reduce((sum, store) => sum + store.lon, 0) / stores.length;
  const lat = stores.reduce((sum, store) => sum + store.lat, 0) / stores.length;
  return { lon, lat };
}

function heleneResourcesCommittedSummary(): string {
  const stores = storesInCone();
  if (stores.length === 0) {
    return 'No CFA stores currently in the Helene forecast cone.';
  }
  const preview = stores
    .slice(0, 5)
    .map((store) => formatStoreListLabel(store))
    .join('; ');
  const more = stores.length > 5 ? ` (+${stores.length - 5} more)` : '';
  return `${stores.length} Chick-fil-A stores in forecast cone: ${preview}${more}`;
}

async function ensureHeleneConeOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === CFA_HELENE_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return existing;
  }

  const { lon, lat } = heleneConeCentroid();
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: MEMPHIS_LINKED_EVENT_NAME,
    type: 'Tropical Storm',
    status: 'Active',
    severity: 'High',
    region: 'Southeast',
    business_unit: 'Chick-fil-A',
    summary:
      'Threshold-triggered Helene event for CFA stores inside the NHC forecast cone.',
    resources_committed: heleneResourcesCommittedSummary(),
    lon,
    lat,
    started_at: '2024-09-24T12:00:00.000Z',
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: MEMPHIS_THRESHOLD_DESCRIPTION,
    demo_key: CFA_HELENE_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

/**
 * When CFA is on, ensure the Helene cone org event exists (idempotent via
 * demo_key). Always runs for the CFA demo path alongside automation foundation
 * rules.
 */
export function useEnsureHeleneConeOrgEvent() {
  const cfaEnabled = useCfaDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cfaEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `helene-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureHeleneConeOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Helene cone org event', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [cfaEnabled, groupGuid, userGuid, queryClient]);
}
